// Unread badge arithmetic, kept pure so it can be tested without a socket,
// a poll timer, or localStorage.
//
// The server is the only place that knows what this user has read on ANY
// device — it computes unread_count from the message_reads table, which the
// phone writes to as well. The browser's own chat_last_read (localStorage)
// cannot see a read that happened on iOS, so it must never be allowed to
// contradict a count the server actually reported. It used to: a stale local
// id turned the server's honest 0 into `unread_count || 1`, which is why a
// thread read on the phone kept a phantom "1" on the web.

/**
 * How many unread messages to badge for one channel.
 *
 * @param {object} info      one channel's entry from GET /api/chat/unread
 * @param {string} [lastReadId] this browser's locally remembered last-read id
 * @returns {number} 0 when nothing should be badged
 */
export function unreadCountFor(info, lastReadId) {
  if (!info || !info.last_message_id) return 0

  // Server reported a count — authoritative, including 0.
  if (typeof info.unread_count === 'number') {
    return info.unread_count > 0 ? info.unread_count : 0
  }

  // Server omitted the field (older backend). Only then does local state
  // get a say, and all it can say is "something is newer than what I saw".
  return lastReadId && lastReadId !== info.last_message_id ? 1 : 0
}

/**
 * True when the server says this channel is fully read but our local
 * last-read id has drifted — i.e. the read happened on another device.
 * Callers adopt info.last_message_id so the two agree from then on.
 */
export function shouldAdoptServerReadPosition(info, lastReadId) {
  return (
    !!info &&
    !!info.last_message_id &&
    info.unread_count === 0 &&
    lastReadId !== info.last_message_id
  )
}
